import { useState } from 'react'
import { BotaoIcone } from './BotaoIcone'
import { useFecharAoClicarFora } from './fechar-fora'
import { IconeEmoji } from './icones'
import { juntar } from './ui'

/**
 * Paleta de emoji do campo de mensagem.
 *
 * Lista escrita à mão em vez de biblioteca: as prontas trazem os 3.700 do
 * padrão Unicode, com nomes e busca, e pesam mais do que o resto da tela
 * inteira. Numa conversa de venda o uso real é um punhado — cumprimento,
 * confirmação, agradecimento — e rolar uma lista de milhares para achar o
 * joinha é mais lento do que a grade curta aqui embaixo.
 *
 * Sem imagem: são caracteres de texto, desenhados pela fonte do sistema. Vão
 * para o WhatsApp como qualquer letra, e continuam funcionando sem internet.
 */

const GRUPOS: { rotulo: string; capa: string; emojis: string[] }[] = [
  {
    rotulo: 'Rostos',
    capa: '🙂',
    emojis: [
      '😀',
      '😃',
      '😄',
      '😁',
      '😊',
      '🙂',
      '😉',
      '😍',
      '🥰',
      '😘',
      '😂',
      '🤣',
      '😅',
      '😆',
      '😌',
      '😔',
      '😞',
      '😟',
      '😕',
      '🙁',
      '😢',
      '😭',
      '😤',
      '😠',
      '😡',
      '🤔',
      '🤨',
      '😐',
      '😶',
      '🙄',
      '😳',
      '🥺',
      '😬',
      '😰',
      '😅',
      '🤗',
      '🤝',
      '😎',
      '🤓',
      '🥳',
    ],
  },
  {
    rotulo: 'Gestos',
    capa: '👍',
    emojis: [
      '👍',
      '👎',
      '👌',
      '🤙',
      '✌️',
      '🤞',
      '🙏',
      '👏',
      '🙌',
      '💪',
      '👋',
      '🤛',
      '🤜',
      '✋',
      '🖐️',
      '👇',
      '👆',
      '👉',
      '👈',
      '☝️',
    ],
  },
  {
    rotulo: 'Símbolos',
    capa: '✅',
    emojis: [
      '✅',
      '❌',
      '⚠️',
      '❗',
      '❓',
      '💯',
      '🔥',
      '⭐',
      '✨',
      '💥',
      '❤️',
      '🧡',
      '💛',
      '💚',
      '💙',
      '💜',
      '🖤',
      '💔',
      '♥️',
      '💖',
      '🔴',
      '🟠',
      '🟡',
      '🟢',
      '🔵',
      '⚫',
      '⚪',
      '🔺',
      '🔻',
      '➡️',
    ],
  },
  {
    rotulo: 'Trabalho',
    capa: '📦',
    emojis: [
      '📦',
      '📄',
      '📃',
      '📋',
      '📊',
      '📈',
      '📉',
      '🧾',
      '💰',
      '💵',
      '💳',
      '🏷️',
      '🛒',
      '🚚',
      '📍',
      '🗓️',
      '⏰',
      '⏳',
      '📌',
      '🔗',
      '📞',
      '📱',
      '💻',
      '🖨️',
      '✉️',
      '📧',
      '🔧',
      '🔩',
      '⚙️',
      '🛠️',
    ],
  },
  {
    rotulo: 'Impressão 3D',
    capa: '🖨️',
    emojis: [
      '🖨️',
      '🧊',
      '🧵',
      '🪛',
      '📐',
      '📏',
      '🔬',
      '🧪',
      '⚗️',
      '🧰',
      '🏭',
      '♻️',
      '🔄',
      '🎯',
      '🧱',
      '🪑',
      '🦾',
      '🤖',
      '💡',
      '🔍',
    ],
  },
  {
    rotulo: 'Festa',
    capa: '🎉',
    emojis: [
      '🎉',
      '🎊',
      '🥂',
      '🍾',
      '🎁',
      '🎂',
      '🏆',
      '🥇',
      '🙌',
      '👑',
      '☀️',
      '🌙',
      '☕',
      '🍀',
      '🌟',
      '🚀',
      '🎈',
      '🌹',
      '😴',
      '👀',
    ],
  },
]

export function SeletorEmoji({ aoEscolher }: { aoEscolher: (emoji: string) => void }) {
  const [aberto, setAberto] = useState(false)
  const [grupo, setGrupo] = useState(0)
  const caixa = useFecharAoClicarFora<HTMLDivElement>(aberto, () => setAberto(false))

  const atual = GRUPOS[grupo] ?? GRUPOS[0]

  return (
    <div ref={caixa} className="relative">
      <BotaoIcone
        rotulo="Emoji"
        onClick={() => setAberto((a) => !a)}
        className={aberto ? 'bg-tinta-100 text-tinta-900' : undefined}
      >
        <IconeEmoji className="h-5 w-5" />
      </BotaoIcone>

      {aberto ? (
        <div className="absolute bottom-full left-0 z-30 mb-1 w-72 rounded-xl ring-1 ring-tinta-200 bg-superficie shadow-media">
          <div className="flex border-b border-tinta-200">
            {GRUPOS.map((g, i) => (
              <button
                key={g.rotulo}
                type="button"
                onClick={() => setGrupo(i)}
                title={g.rotulo}
                aria-label={g.rotulo}
                aria-pressed={i === grupo}
                className={juntar(
                  'flex-1 py-1.5 text-base',
                  i === grupo ? 'border-b-2 border-marca bg-marca/5' : 'hover:bg-tinta-50',
                )}
              >
                {g.capa}
              </button>
            ))}
          </div>

          <div className="grid max-h-52 grid-cols-8 gap-0.5 overflow-y-auto p-1.5">
            {(atual?.emojis ?? []).map((e, i) => (
              <button
                // O mesmo emoji aparece em mais de um grupo de propósito; a
                // posição entra na chave para o React não reclamar disso.
                key={`${e}-${i}`}
                type="button"
                onClick={() => aoEscolher(e)}
                className="rounded p-1 text-xl leading-none hover:bg-tinta-100"
              >
                {e}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}
